/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { baseApiService } from './base'

export interface Watermark {
  id: number
  name: string
  description: string | null
  system: boolean
  createdAt: string
  createdBy: {
    id: number
    name: string
  } | null
  updatedAt: string
  updatedBy: {
    id: number
    name: string
  } | null
  text: string
  textAlignment: number
  verticalAlignment: number
  fontStyle: number
  fontSize: number
  fontColor: string
  rotation: number
  transparency: number
  [key: string]: any
}

export interface WatermarksResponse {
  elements: Watermark[]
  totalElements: number
  totalPages: number
  pageSize: number
  pageNumber: number
}

export interface WatermarkPickerOption {
  id: number
  name: string
  value: number
  label: string
}

export interface AsyncLoadResponse {
  options: WatermarkPickerOption[]
  hasMore: boolean
}

export interface WatermarkPickerResponse {
  elements: WatermarkPickerOption[]
  totalElements: number
  hasMore: boolean
}

export interface WatermarkParams {
  pageSize?: number
  pageStart?: number
  sortBy?: string
  sortDesc?: boolean
  id?: number[]
  [key: string]: string | number | boolean | number[] | undefined
}

/**
 * Get all watermarks with pagination
 */
export async function getAllWatermarks(params: WatermarkParams = {}): Promise<WatermarksResponse> {
  return baseApiService.fetch<WatermarksResponse>('/api/v2/watermarks', {
    params
  })
}

/**
 * Get watermarks for async picker (dropdown)
 */
export async function getWatermarksForPicker(search: string = '', pageStart: number = 0): Promise<AsyncLoadResponse> {
  const params = {
    name: search || undefined,
    pageStart,
    pageSize: 25
  }

  const response = await baseApiService.fetch<WatermarksResponse>('/api/v2/watermarks/picker', {
    params
  })

  // Calculate if there are more pages
  const hasMore = response.elements.length > 0 && (pageStart + 1) * 25 < response.totalElements

  // Map and sort options by label (case-insensitive)
  const options = response.elements
    .map((w) => ({
      value: w.id,
      label: w.name,
      id: w.id,
      name: w.name
    }))
    .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))

  return {
    options,
    hasMore
  }
}

/**
 * Get all watermarks for picker (sorted by name)
 */
export async function getAllWatermarksForPicker(): Promise<WatermarkPickerOption[]> {
  const response = await baseApiService.fetch<WatermarkPickerOption[]>('/api/watermarks/all')
  // Sort by name (case-insensitive)
  return response.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
}

/**
 * Get watermark by ID
 */
export async function getWatermarkById(watermarkId: number): Promise<Watermark> {
  return baseApiService.fetch<Watermark>(`/api/v2/watermarks/${watermarkId}`)
}

/**
 * Create a new watermark
 */
export async function createWatermark(data: Partial<Watermark>): Promise<Watermark> {
  return baseApiService.fetch<Watermark>('/api/watermarks', {
    method: 'POST',
    body: data
  })
}

/**
 * Update an existing watermark
 */
export async function updateWatermark(watermarkId: number, data: Partial<Watermark>): Promise<Watermark> {
  return baseApiService.fetch<Watermark>(`/api/watermarks/${watermarkId}`, {
    method: 'PUT',
    body: data
  })
}

/**
 * Delete a watermark
 */
export async function deleteWatermark(watermarkId: number): Promise<void> {
  return baseApiService.fetch<void>(`/api/watermarks/${watermarkId}`, {
    method: 'DELETE'
  })
}

/**
 * Get watermark usage by ID
 */
export async function getWatermarkUsage(watermarkId: number): Promise<any> {
  return baseApiService.fetch(`/api/watermarks/${watermarkId}/usage`)
}
